// ---------------------------------------------------------------------------
// Income Calculator constants
// Re-exports the existing PAY_FREQUENCY enum from the parent constants module
// (single source of truth — do not fork).
// ---------------------------------------------------------------------------

// The canonical PAY_FREQUENCY values live in Signers.schema.ts payFrequency
// enum — we derive the type from there and mirror the object here so the
// calculator module has its own self-contained constant without duplication.
export const PAY_FREQUENCY = {
  ANNUAL: "Annual",
  BI_WEEKLY: "Bi-Weekly",
  MONTHLY: "Monthly",
  TWICE_MONTHLY: "2x-Monthly",
  WEEKLY: "Weekly",
} as const;

export type PayFrequency = (typeof PAY_FREQUENCY)[keyof typeof PAY_FREQUENCY];

// ---------------------------------------------------------------------------
// Frequency multipliers (matches dealer-ui / CAPS2 exactly)
// Weekly → 4.33333 pay periods per month
// Bi-Weekly → 2.16666
// 2x-Monthly → 2
// Monthly → 1
// Annual → 0.08333 (1/12)
// ---------------------------------------------------------------------------
export const PAY_FREQUENCY_FACTOR: Record<PayFrequency, number> = {
  [PAY_FREQUENCY.WEEKLY]: 4.333_33,
  [PAY_FREQUENCY.BI_WEEKLY]: 2.166_66,
  [PAY_FREQUENCY.TWICE_MONTHLY]: 2,
  [PAY_FREQUENCY.MONTHLY]: 1,
  [PAY_FREQUENCY.ANNUAL]: 0.083_33,
};

// ---------------------------------------------------------------------------
// Net-pay gross-up factor (parity spec: netPayFactor 1.21).
// When payType is "Net", both the income basis and the tax basis are multiplied
// by this factor before all calculations so they are on a consistent Gross basis.
// ---------------------------------------------------------------------------
export const NET_PAY_FACTOR = 1.21;

// ---------------------------------------------------------------------------
// Tax rates (date-aware parity with CAPS2 Step2Applicant.calculateTaxes)
// Social Security: 4.2% for pay dates before 2013, 6.2% for 2013+.
// Medicare: flat 1.45% regardless of date.
// ---------------------------------------------------------------------------
export const TAX_RATES = {
  SS_PRE_2013: 0.042,
  SS_2013_PLUS: 0.062,
  MEDICARE: 0.0145,
} as const;

// ---------------------------------------------------------------------------
// Income ratio warning thresholds (parity spec / dealer-ui).
// When ratio = monthlyIncomePeriod / monthlyIncomeYtd falls outside
// [LOW, HIGH], a warning banner is shown in the calculator panel.
// ---------------------------------------------------------------------------
export const INCOME_RATIO = {
  LOW: 0.9,
  HIGH: 1.1,
} as const;
